import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Dialog, DialogContent, DialogActions, Button,
    Typography, Box, Stack, IconButton, LinearProgress, Chip, Avatar,
    Fade, Slide, Zoom, useTheme, alpha, Tooltip
} from '@mui/material';
import {
    Close as CloseIcon,
    ArrowForward as NextIcon,
    ArrowBack as BackIcon,
    EmojiEvents as TrophyIcon,
    Lightbulb as TipIcon,
    Replay as ReplayIcon
} from '@mui/icons-material';

const ONBOARDING_STEPS = [
    {
        label: 'Bem-vindo',
        icon: '👋',
        title: 'Bem-vindo ao SACH! 🎉',
        subtitle: 'Sistema de Alocação de Custos Hospitalares',
        description: 'Vamos fazer um tour rápido pelo sistema em apenas 5 minutos.',
        highlights: [
            { icon: '📊', text: 'Gestão de custos' },
            { icon: '🤖', text: 'Análises com IA' },
            { icon: '📈', text: 'Relatórios automáticos' }
        ],
        tips: 'Você pode refazer este tour a qualquer momento.',
        page: null
    },
    {
        label: 'Upload',
        icon: '📁',
        title: 'Passo 1: Carregar Dados',
        subtitle: 'Alimentando o sistema',
        description: 'O SACH precisa de Balancete (custos) e Produção (procedimentos).',
        highlights: [
            { icon: '💰', text: 'Balancete contábil' },
            { icon: '🏥', text: 'Dados de produção' }
        ],
        tips: 'Use formato Excel (.xlsx) para melhor compatibilidade.',
        page: '/processamento'
    },
    {
        label: 'Processar',
        icon: '⚙️',
        title: 'Passo 2: Processar',
        subtitle: 'Transformando dados',
        description: 'Processe a competência (mês/ano) para calcular custos.',
        highlights: [
            { icon: '🔄', text: 'Cruza custos com produção' },
            { icon: '💾', text: 'Gera histórico' }
        ],
        tips: 'Processamento leva 5-15 segundos.',
        page: '/processamento'
    },
    {
        label: 'Regras',
        icon: '📊',
        title: 'Passo 3: Regras de Rateio',
        subtitle: 'Distribuição de custos (opcional)',
        description: 'Configure como distribuir custos de centros de apoio.',
        highlights: [
            { icon: '%', text: 'Percentual Fixo' },
            { icon: '🔀', text: 'Step-Down' }
        ],
        tips: 'Pule este passo se não tiver centros de apoio.',
        page: '/alocacao-custos'
    },
    {
        label: 'Relatórios',
        icon: '📄',
        title: 'Passo 4: Relatórios',
        subtitle: 'Visualize resultados',
        description: 'Gere relatórios gerenciais exportáveis.',
        highlights: [
            { icon: '✅', text: 'Conferência' },
            { icon: '💼', text: 'DRE por Centro' }
        ],
        tips: 'Exporte para Excel com um clique!',
        page: '/relatorio-conferencia'
    },
    {
        label: 'IA',
        icon: '🤖',
        title: 'Passo 5: Análises com IA',
        subtitle: 'Insights automáticos',
        description: 'Detecte padrões e obtenha recomendações.',
        highlights: [
            { icon: '🔍', text: 'Insights automáticos' },
            { icon: '📈', text: 'Previsões' }
        ],
        tips: 'As análises melhoram com mais dados!',
        page: '/ai-analysis'
    },
    {
        label: 'Concluído',
        icon: '🎉',
        title: 'Parabéns! 🏆',
        subtitle: 'Tour concluído',
        description: 'Você conhece todo o fluxo do sistema!',
        highlights: [
            { icon: '✅', text: 'Upload de dados' },
            { icon: '✅', text: 'Processamento' },
            { icon: '✅', text: 'Relatórios e IA' }
        ],
        tips: 'Atalho: Ctrl+K para busca universal!',
        page: null
    }
];

const OnboardingTour = () => {
    const [open, setOpen] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [completed, setCompleted] = useState(false);
    const [animateContent, setAnimateContent] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();

    // Cores do sistema
    const primaryColor = theme.palette.primary.main;
    const secondaryColor = theme.palette.secondary.main;

    useEffect(() => {
        const hasSeenOnboarding = localStorage.getItem('sach-onboarding-completed');
        if (!hasSeenOnboarding) {
            const timer = setTimeout(() => setOpen(true), 1500);
            return () => clearTimeout(timer);
        }
    }, []);

    useEffect(() => {
        if (open) {
            setAnimateContent(false);
            const timer = setTimeout(() => setAnimateContent(true), 100);
            return () => clearTimeout(timer);
        }
    }, [activeStep, open]);

    const handleNext = () => {
        const currentStep = ONBOARDING_STEPS[activeStep];
        if (currentStep.page && location.pathname !== currentStep.page) {
            navigate(currentStep.page);
        }

        if (activeStep < ONBOARDING_STEPS.length - 1) {
            setActiveStep(activeStep + 1);
        } else {
            setCompleted(true);
            localStorage.setItem('sach-onboarding-completed', 'true');
            setTimeout(() => setOpen(false), 500);
        }
    };

    const handleBack = () => setActiveStep(activeStep - 1);

    const handleClose = () => {
        setOpen(false);
        if (completed || activeStep === ONBOARDING_STEPS.length - 1) {
            localStorage.setItem('sach-onboarding-completed', 'true');
        }
    };

    const handleRestart = () => {
        setActiveStep(0);
        setCompleted(false);
        setOpen(true);
    };

    const progress = ((activeStep + 1) / ONBOARDING_STEPS.length) * 100;
    const currentStep = ONBOARDING_STEPS[activeStep];

    // Cores baseadas no tema do sistema
    const stepColor = theme.palette.mode === 'dark'
        ? alpha(primaryColor, 0.8)
        : primaryColor;

    return (
        <>
            {/* Botão compacto para refazer tour */}
            {!open && (
                <Zoom in={!open}>
                    <Tooltip title="Refazer Tour" placement="left">
                        <IconButton
                            onClick={handleRestart}
                            size="small"
                            sx={{
                                position: 'fixed',
                                bottom: 180,
                                right: 24,
                                zIndex: 1299,
                                width: 36,
                                height: 36,
                                bgcolor: alpha(primaryColor, 0.1),
                                backdropFilter: 'blur(8px)',
                                border: `1px solid ${alpha(primaryColor, 0.2)}`,
                                color: primaryColor,
                                '&:hover': {
                                    bgcolor: alpha(primaryColor, 0.2),
                                    transform: 'scale(1.1)',
                                },
                                transition: 'all 0.3s ease'
                            }}
                        >
                            <ReplayIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Zoom>
            )}

            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                TransitionComponent={Slide}
                TransitionProps={{ direction: 'up' }}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        maxHeight: '85vh',
                        overflow: 'hidden',
                        bgcolor: alpha(theme.palette.background.paper, 0.95),
                        backdropFilter: 'blur(20px)',
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        boxShadow: `0 25px 50px -12px ${alpha('#000', 0.25)}`
                    }
                }}
            >
                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                    {/* Header compacto */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Avatar
                                sx={{
                                    width: { xs: 44, sm: 52 },
                                    height: { xs: 44, sm: 52 },
                                    bgcolor: alpha(stepColor, 0.15),
                                    fontSize: { xs: '1.5rem', sm: '1.8rem' },
                                    border: `2px solid ${alpha(stepColor, 0.3)}`
                                }}
                            >
                                {currentStep.icon}
                            </Avatar>
                            <Box>
                                <Typography
                                    variant="h6"
                                    fontWeight="bold"
                                    sx={{
                                        fontSize: { xs: '1rem', sm: '1.15rem' },
                                        color: 'text.primary'
                                    }}
                                >
                                    {currentStep.title}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}
                                >
                                    {currentStep.subtitle}
                                </Typography>
                            </Box>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Chip
                                label={`${activeStep + 1}/${ONBOARDING_STEPS.length}`}
                                size="small"
                                sx={{
                                    fontWeight: 'bold',
                                    fontSize: '0.7rem',
                                    height: 24,
                                    bgcolor: alpha(stepColor, 0.1),
                                    color: stepColor
                                }}
                            />
                            <IconButton onClick={handleClose} size="small">
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    </Stack>

                    {/* Progress bar suave */}
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            height: 4,
                            borderRadius: 2,
                            mb: 2,
                            bgcolor: alpha(stepColor, 0.1),
                            '& .MuiLinearProgress-bar': {
                                bgcolor: stepColor,
                                borderRadius: 2
                            }
                        }}
                    />

                    {/* Conteúdo responsivo sem scroll */}
                    <Fade in={animateContent} timeout={300}>
                        <DialogContent sx={{ p: 0 }}>
                            <Box
                                sx={{
                                    p: 2,
                                    bgcolor: alpha(stepColor, 0.03),
                                    borderRadius: 2,
                                    border: `1px solid ${alpha(stepColor, 0.08)}`
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        mb: 2,
                                        color: 'text.primary',
                                        lineHeight: 1.6
                                    }}
                                >
                                    {currentStep.description}
                                </Typography>

                                {/* Highlights compactos em grid */}
                                {currentStep.highlights && (
                                    <Stack
                                        direction="row"
                                        flexWrap="wrap"
                                        gap={1}
                                        sx={{ mb: 2 }}
                                    >
                                        {currentStep.highlights.map((item, idx) => (
                                            <Chip
                                                key={idx}
                                                avatar={
                                                    <Avatar
                                                        sx={{
                                                            bgcolor: 'transparent',
                                                            fontSize: '0.9rem'
                                                        }}
                                                    >
                                                        {item.icon}
                                                    </Avatar>
                                                }
                                                label={item.text}
                                                size="small"
                                                sx={{
                                                    bgcolor: alpha(stepColor, 0.08),
                                                    border: `1px solid ${alpha(stepColor, 0.15)}`,
                                                    fontSize: '0.75rem',
                                                    '& .MuiChip-avatar': {
                                                        width: 24,
                                                        height: 24
                                                    }
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                )}

                                {/* Dica compacta */}
                                {currentStep.tips && (
                                    <Box
                                        sx={{
                                            p: 1.5,
                                            bgcolor: alpha(theme.palette.warning.main, 0.08),
                                            borderRadius: 1.5,
                                            borderLeft: `3px solid ${alpha(theme.palette.warning.main, 0.5)}`,
                                            display: 'flex',
                                            gap: 1,
                                            alignItems: 'center'
                                        }}
                                    >
                                        <TipIcon sx={{ color: theme.palette.warning.main, fontSize: 18 }} />
                                        <Typography variant="caption" color="text.secondary">
                                            {currentStep.tips}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            {/* Indicadores de passo */}
                            <Box mt={2} sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                                {ONBOARDING_STEPS.map((step, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            width: index === activeStep ? 24 : 8,
                                            height: 8,
                                            borderRadius: 4,
                                            bgcolor: index === activeStep
                                                ? stepColor
                                                : alpha(stepColor, 0.2),
                                            transition: 'all 0.3s ease',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => setActiveStep(index)}
                                    />
                                ))}
                            </Box>
                        </DialogContent>
                    </Fade>

                    {/* Ações compactas */}
                    <DialogActions sx={{ px: 0, pt: 2, gap: 1 }}>
                        <Button
                            onClick={handleBack}
                            disabled={activeStep === 0}
                            startIcon={<BackIcon />}
                            size="small"
                            sx={{ color: 'text.secondary' }}
                        >
                            Voltar
                        </Button>
                        <Box sx={{ flex: 1 }} />
                        <Button
                            onClick={handleClose}
                            color="inherit"
                            size="small"
                        >
                            Pular
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleNext}
                            endIcon={activeStep === ONBOARDING_STEPS.length - 1 ? <TrophyIcon /> : <NextIcon />}
                            size="small"
                            sx={{
                                bgcolor: stepColor,
                                px: 2.5,
                                '&:hover': {
                                    bgcolor: alpha(stepColor, 0.85)
                                }
                            }}
                        >
                            {activeStep === ONBOARDING_STEPS.length - 1 ? 'Concluir' : 'Próximo'}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>
        </>
    );
};

export default OnboardingTour;
